import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  SafeHtmlPipe,
  SiaI18nService,
  SiaSelectBoxDataService,
  SiaSignalrService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { Subscription } from 'rxjs';
import { SelectBoxOptions } from '../../interfaces/select-box-options';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';

@Component({
  selector: 'button-select-box',
  standalone: true,
  imports: [
    ScrollingModule,
    SiaTooltipDirective,
    SiaTranslatePipe,
    SafeHtmlPipe,
    MatDivider,
    MatIconButton,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    MatRipple,
  ],
  templateUrl: './button-select-box.component.html',
  styleUrl: './button-select-box.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonSelectBoxComponent extends SiaBaseEditorSignalComponent {
  // Services
  #selectBoxDataService = inject(SiaSelectBoxDataService);
  #siaSignalrService = inject(SiaSignalrService);
  #i18nService = inject(SiaI18nService);

  // Inputs using signals
  options = input<SelectBoxOptions>({});
  emptyLabel = input<string>('');
  extraButtonLabel = input<string>('');
  extraButtonPosition = input<'top' | 'bottom'>('top');
  extraButtonIcon = input<string | undefined>(undefined);
  useShortDescription = input<boolean>(false);

  // Computed values prioritizing explicit inputs over options
  computedEmptyLabel = computed(() => {
    const explicit = this.emptyLabel();
    if (explicit !== undefined && explicit !== null && explicit !== '') {
      return this.#i18nService.translate(explicit);
    }
    const opts = this.options();
    return this.#i18nService.translate(opts?.emptyLabel || '');
  });

  computedExtraButtonLabel = computed(() => {
    const explicit = this.extraButtonLabel();
    if (explicit !== undefined && explicit !== null && explicit !== '') {
      return this.#i18nService.translate(explicit);
    }
    const opts = this.options();
    return this.#i18nService.translate(opts?.extraButtonLabel || '');
  });

  computedExtraButtonPosition = computed(() => {
    const explicit = this.extraButtonPosition();
    const opts = this.options();
    return opts?.extraButtonPosition || explicit;
  });

  extraIcon = computed(() => {
    const explicit = this.extraButtonIcon();

    if (explicit !== undefined && explicit !== null && explicit !== '') {
      return explicit;
    }
    const opts = this.options();
    return opts && opts.extraButtonIcon ? opts.extraButtonIcon : undefined;
  });

  computedRemoveSelection = computed(() => {
    const explicit = this.removeSelection();
    const opts = this.options();
    return opts?.removeSelection ?? explicit;
  });

  computedRemoveSelectionLabel = computed(() => {
    const explicit = this.removeSelectionLabel();
    if (explicit !== undefined && explicit !== null && explicit !== '') {
      return this.#i18nService.translate(explicit);
    }
    const opts = this.options();
    return this.#i18nService.translate(
      opts?.removeSelectionLabel || 'Rimuovi selezione',
    );
  });

  useShortDescriptionComputed = computed(() => {
    const explicit = this.useShortDescription();
    const opts = this.options();
    return opts?.useShortDescription ?? explicit;
  });

  extraIconStyle = computed(() => {
    const icon = this.extraIcon();
    if (!icon) return '';
    const errorIcons = [
      'cancel',
      'delete',
      'logout',
      'error',
      'error_outline',
      'remove',
      'remove_circle',
      'close',
      'highlight_off',
    ];
    const successIcons = ['login', 'confirm', 'check_circle', 'done', 'add'];

    if (errorIcons.includes(icon)) {
      return 'color: var(--sia-error) !important;';
    }
    if (successIcons.includes(icon)) {
      return 'color: var(--sia-success) !important;';
    }
    return '';
  });

  translateData = computed(() => {
    const opts = this.options();
    return opts?.translateData || false;
  });

  removeSelection = input<boolean>(false);
  removeSelectionLabel = input<string>('Rimuovi selezione');
  mobile = input<boolean>(false);

  // Outputs
  selectionChanged = output<any>();
  extraButtonClicked = output<void>();
  loadedDataSource = output<boolean>();

  // Internal state
  dataSource = signal<any[]>([]);
  loading = signal<boolean>(false);
  menuOpen = signal<boolean>(false);
  searchQuery = signal<string>('');
  #subs: Subscription[] = [];

  showSearch = computed(() => this.dataSource().length > 10);

  filteredDataSource = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const data = this.dataSource();
    if (!query) return data;
    return data.filter(
      (item) =>
        item.description?.toLowerCase().includes(query) ||
        String(item.id).toLowerCase().includes(query),
    );
  });

  readonly #menuCloseEffect = effect(() => {
    if (!this.menuOpen()) {
      this.searchQuery.set('');
    }
  });

  #loadingEffect = effect(() => {
    this.loadedDataSource.emit(!this.loading());
  });

  // Computed display value
  displayValue = computed(() => {
    const val = this.value();
    if (val === undefined || val === null || val === '') {
      return this.computedEmptyLabel();
    }
    const selectedItem = this.dataSource().find((item) => item.id == val);
    if (selectedItem) {
      const labelText = this.label();
      const shortDesc = this.useShortDescriptionComputed();
      return labelText
        ? `${labelText} - ${shortDesc ? selectedItem.id : selectedItem.description}`
        : shortDesc
          ? selectedItem.id
          : selectedItem.description;
    }
    return this.computedEmptyLabel();
  });

  // Computed selected item
  selectedItem = computed(() => {
    const val = this.value();
    if (val === undefined || val === null || val === '') {
      return undefined;
    }
    return this.dataSource().find((item) => item.id == val);
  });

  #autoSelectFirst = effect(() => {
    const data = this.dataSource();
    const val = this.value();
    const opts = this.options();
    if (
      opts?.autoSelectFirst &&
      data.length > 0 &&
      (val === undefined || val === null || val === '')
    ) {
      this.value.set(data[0].id);
      this.selectionChanged.emit(data[0]);
    }
  });

  #stopEffect?: EffectRef;
  @ViewChild(CdkVirtualScrollViewport)
  protected _viewport?: CdkVirtualScrollViewport;

  onMenuOpened() {
    this.menuOpen.set(true);
    const selectedIndex = this.filteredDataSource().findIndex(
      (item) => item.id === this.value(),
    );
    setTimeout(() => {
      this._viewport?.checkViewportSize();
      if (selectedIndex > 0) {
        const itemHeight = 48;
        const viewportHeight = this._viewport?.getViewportSize() ?? 0;
        const offset =
          selectedIndex * itemHeight - viewportHeight / 2 + itemHeight / 2;
        this._viewport?.scrollToOffset(Math.max(0, offset), 'smooth');
      }
    });
  }

  readonly viewportHeight = computed(() => {
    const count = this.filteredDataSource().length;
    const itemHeight = 48;
    let reserved = 0;
    if (this.showSearch()) reserved += 52; // search bar
    if (this.computedRemoveSelection()) reserved += 49; // remove button
    const maxPx = window.innerHeight * 0.5 - reserved; // max metà schermo
    return Math.min(count * itemHeight, maxPx);
  });

  constructor() {
    super();
    this.#stopEffect = effect(
      () => {
        this.loadData();
      },
      { allowSignalWrites: true },
    );
  }

  getMarkerType(marker: string) {
    if (!marker || marker == '') return undefined;
    const colorRegex =
      /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|rgb|hsl|var\(|white|black|blue|red|green|yellow|orange|purple|pink|gray|grey|brown|cyan|magenta|lime|navy|teal|olive|maroon|aqua|silver|gold)$/i;
    const svgRegex = /\.(svg)$|<svg/i;
    const iconRegex = /^[a-z_]+$/;

    if (colorRegex.test(marker)) {
      return 'color';
    }
    if (svgRegex.test(marker)) {
      return 'vectorImg';
    }
    if (iconRegex.test(marker)) {
      return 'icon';
    }
    return undefined;
  }

  async loadData() {
    const opts = this.options();
    if (!opts) return;

    if (Array.isArray(opts)) {
      this.dataSource.set(opts);
      return;
    }

    if (opts.fixedDataSource) {
      this.dataSource.set(opts.fixedDataSource);
      return;
    }

    if (opts.dbKey) {
      this.loading.set(true);
      try {
        const data = await this.#selectBoxDataService.loadFullData(
          opts.dbKey,
          opts.foreignKey || '',
          opts.orderByKey ? true : false,
        );

        this.dataSource.set(data || []);
      } finally {
        this.loading.set(false);
      }
    }
  }

  selectValue(item: any) {
    this.value.set(item.id);
    this.selectionChanged.emit(item);
  }

  clearValue() {
    this.value.set(undefined);
    this.selectionChanged.emit(undefined);
  }

  onExtraButtonClick() {
    this.extraButtonClicked.emit();
  }

  ngOnInit() {
    let s = this.#siaSignalrService.refreshCollectionsMessage$.subscribe(
      (messageKeys: { [key: string]: boolean }) => {
        const opts = this.options();
        if (!opts || !opts.dbKey) return;

        const dbKeyLower = opts.dbKey.toLowerCase();
        const messageKeysLower = Object.keys(messageKeys).map((key) =>
          key.toLowerCase(),
        );

        if (
          messageKeysLower.some((key: string) => dbKeyLower.includes(key)) ||
          messageKeys['*']
        ) {
          this.loadData();
        }
      },
    );
    this.#subs.push(s);
  }

  ngOnDestroy() {
    this.#subs.forEach((s) => s.unsubscribe());
    this.#stopEffect?.destroy?.();
  }
}
